import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "../use-access-token";
import {
  EXTENDED_CACHE_TIME,
  type ReferenceRecord,
  referenceRecordsToLookup,
  referenceRecordToCodeLookup,
  SourceRecord
} from "../use-reference-data";
import { callLcosService } from "../../data/lcosServices";
import { CODES } from "../../data/dynamics-codes";

interface InquiryConstants {
  appointmentStatuses: ReferenceRecord[];
  appointmentCancelChoices: ReferenceRecord[];
  discountReasons: ReferenceRecord[];
  discountTypes: ReferenceRecord[];
  referralSources: ReferenceRecord[];
  contactMethodSources: SourceRecord[];
}

export const useInquiryConstants = () => {
  const { getToken } = useAccessToken();

  return useQuery({
    queryKey: ["inquiryConstants"] as const,
    queryFn: async () => getInquiryConstants(await getToken()),
    cacheTime: EXTENDED_CACHE_TIME,
    staleTime: EXTENDED_CACHE_TIME,
    select: data => {
      const APPOINTMENT_STATUS_CODE_LOOKUP = referenceRecordToCodeLookup(data.appointmentStatuses);
      const APPOINTMENT_STATUS_SCHEDULED_ID = APPOINTMENT_STATUS_CODE_LOOKUP[CODES.APPOINTMENT_STATUSES.SCHEDULED]
        .value as string;
      const APPOINTMENT_STATUS_RESCHEDULED_ID = "RESCHEDULED";
      const APPOINTMENT_STATUS_AS_SCHEDULED_ID = APPOINTMENT_STATUS_CODE_LOOKUP[CODES.APPOINTMENT_STATUSES.AS_SCHEDULED]
        .value as string;
      const APPOINTMENT_STATUS_CANCELLED_ID = APPOINTMENT_STATUS_CODE_LOOKUP[CODES.APPOINTMENT_STATUSES.CANCELLED]
        .value as string;
      const APPOINTMENT_STATUS_NO_SHOW_ID = APPOINTMENT_STATUS_CODE_LOOKUP[CODES.APPOINTMENT_STATUSES.NO_SHOW]
        .value as string;

      const APPOINTMENT_CANCEL_REASON_LOOKUP = referenceRecordToCodeLookup(data.appointmentCancelChoices);
      const APPOINTMENT_CANCEL_REASON_RESCHEDULED_ID = APPOINTMENT_CANCEL_REASON_LOOKUP[
        CODES.APPOINTMENT_CANCEL_REASONS.RESCHEDULED
      ].value as string;
      const APPOINTMENT_CANCEL_REASON_OTHER_ID = APPOINTMENT_CANCEL_REASON_LOOKUP[
        CODES.APPOINTMENT_CANCEL_REASONS.OTHER
      ].value as string;

      const DISCOUNT_REASON_CODE_LOOKUP = referenceRecordToCodeLookup(data.discountReasons);
      const DISCOUNT_REASON_OTHER_ID = DISCOUNT_REASON_CODE_LOOKUP[CODES.DISCOUNT_REASONS.OTHER].value as string;

      return {
        ...data,
        appointmentStatusesLookup: referenceRecordsToLookup(data.appointmentStatuses),
        activeAppointmentStatuses: data.appointmentStatuses.filter(appointmentStatus => appointmentStatus.isActive),
        activeAppointmentCancelChoices: data.appointmentCancelChoices.filter(
          appointmentCancelChoice =>
            appointmentCancelChoice.isActive &&
            appointmentCancelChoice.value !== APPOINTMENT_CANCEL_REASON_RESCHEDULED_ID
        ),
        activeDiscountReasons: data.discountReasons.filter(discountReason => discountReason.isActive),
        activeDiscountTypes: data.discountTypes.filter(discountType => discountType.isActive),
        activeReferralSources: data.referralSources.filter(referralSource => referralSource.isActive),
        contactMethodSources: data.contactMethodSources.filter(contactMethodSource => contactMethodSource.isActive),
        APPOINTMENT_STATUS_CODE_LOOKUP,
        APPOINTMENT_STATUS_SCHEDULED_ID,
        APPOINTMENT_STATUS_RESCHEDULED_ID,
        APPOINTMENT_STATUS_AS_SCHEDULED_ID,
        APPOINTMENT_STATUS_CANCELLED_ID,
        APPOINTMENT_STATUS_NO_SHOW_ID,
        APPOINTMENT_CANCEL_REASON_RESCHEDULED_ID,
        APPOINTMENT_CANCEL_REASON_OTHER_ID,
        DISCOUNT_REASON_OTHER_ID
      };
    }
  });
};

export const getInquiryConstants = (accessToken: string) => {
  return callLcosService<InquiryConstants>(accessToken, `/api/data/constants/inquiries`, "GET");
};
